import { useState } from "react";

/**
 * Lightweight, dependency-free emoji picker.
 * Curated categories, theme-token styling, dark-mode safe.
 * Props:
 *   - onSelect(emoji): called when an emoji is chosen
 */
const EMOJI_CATEGORIES = [
  {
    key: "smileys",
    label: "Smileys",
    icon: "😀",
    emojis: [
      "😀", "😃", "😄", "😁", "😆", "😅", "😂", "🤣", "🙂", "🙃",
      "😉", "😊", "😇", "🥰", "😍", "🤩", "😘", "😗", "😚", "😙",
      "😋", "😛", "😜", "🤪", "😝", "🤑", "🤗", "🤭", "🤫", "🤔",
      "😐", "😑", "😶", "😏", "😒", "🙄", "😬", "😌", "😔", "😪",
      "😴", "😷", "🤒", "🤕", "🤧", "🥵", "🥶", "😵", "🤯", "🥳",
      "😎", "🤓", "🧐", "😕", "😟", "🙁", "😮", "😯", "😲", "😳",
      "🥺", "😦", "😨", "😰", "😢", "😭", "😱", "😖", "😣", "😞",
      "😓", "😩", "😫", "😤", "😡", "😠", "🤬", "😈", "👿", "💀",
    ],
  },
  {
    key: "gestures",
    label: "Gestures",
    icon: "👍",
    emojis: [
      "👍", "👎", "👌", "🤌", "🤏", "✌️", "🤞", "🤟", "🤘", "🤙",
      "👈", "👉", "👆", "👇", "☝️", "👋", "🤚", "🖐️", "✋", "🖖",
      "👏", "🙌", "👐", "🤲", "🙏", "🤝", "💪", "🦾", "✍️", "💅",
      "👀", "👁️", "👂", "👃", "🧠", "🫀", "🦷", "👅", "👄", "🫶",
    ],
  },
  {
    key: "hearts",
    label: "Hearts",
    icon: "❤️",
    emojis: [
      "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "🤎", "💔",
      "❣️", "💕", "💞", "💓", "💗", "💖", "💘", "💝", "💟", "♥️",
      "🔥", "✨", "⭐", "🌟", "💫", "⚡", "💥", "💯", "🎉", "🎊",
    ],
  },
  {
    key: "animals",
    label: "Animals",
    icon: "🐶",
    emojis: [
      "🐶", "🐱", "🐭", "🐹", "🐰", "🦊", "🐻", "🐼", "🐨", "🐯",
      "🦁", "🐮", "🐷", "🐸", "🐵", "🐔", "🐧", "🐦", "🐤", "🦄",
      "🐝", "🦋", "🐌", "🐞", "🐢", "🐙", "🐠", "🐬", "🐳", "🦈",
      "🌸", "🌺", "🌻", "🌷", "🌹", "🌴", "🌵", "🍀", "🍁", "🌍",
    ],
  },
  {
    key: "food",
    label: "Food",
    icon: "🍔",
    emojis: [
      "🍎", "🍌", "🍇", "🍓", "🍑", "🍒", "🍉", "🍊", "🥭", "🍍",
      "🍔", "🍟", "🍕", "🌭", "🥪", "🌮", "🌯", "🍿", "🧂", "🍦",
      "🍩", "🍪", "🎂", "🍰", "🧁", "🍫", "🍬", "🍭", "🍮", "☕",
      "🍵", "🥤", "🍺", "🍻", "🥂", "🍷", "🥃", "🍸", "🍹", "🧊",
    ],
  },
  {
    key: "activities",
    label: "Activity",
    icon: "⚽",
    emojis: [
      "⚽", "🏀", "🏈", "⚾", "🎾", "🏐", "🏉", "🎱", "🏓", "🏸",
      "🥅", "🏆", "🥇", "🥈", "🥉", "🎯", "🎮", "🎲", "🎸", "🎺",
      "🎻", "🥁", "🎤", "🎧", "🎬", "🎨", "✈️", "🚀", "🚗", "🏖️",
    ],
  },
  {
    key: "objects",
    label: "Objects",
    icon: "💡",
    emojis: [
      "💡", "🔦", "📱", "💻", "⌚", "📷", "🎥", "📺", "🔋", "💸",
      "💰", "💳", "📦", "✉️", "📌", "📎", "✂️", "🔑", "🔒", "🔓",
      "🛎️", "⏰", "⏳", "📅", "📈", "📉", "📊", "📝", "✅", "❌",
    ],
  },
  {
    key: "symbols",
    label: "Symbols",
    icon: "🔣",
    emojis: [
      "✅", "❎", "✔️", "❗", "❓", "‼️", "⁉️", "💲", "©️", "®️",
      "™️", "🔝", "🔙", "🔜", "🆗", "🆕", "🆒", "🚫", "⛔", "♻️",
      "⚠️", "🔔", "🔕", "➕", "➖", "➗", "✖️", "♾️", "💤", "🎵",
    ],
  },
];

const EmojiPicker = ({ onSelect }) => {
  const [activeKey, setActiveKey] = useState(EMOJI_CATEGORIES[0].key);
  const active = EMOJI_CATEGORIES.find((c) => c.key === activeKey) ?? EMOJI_CATEGORIES[0];

  return (
    <div
      className="w-[290px] bg-surface border border-border rounded-2xl
        shadow-[0_12px_32px_rgba(0,0,0,0.18)] overflow-hidden slide-in"
    >
      {/* Category tabs */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-border bg-surface_alt">
        {EMOJI_CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            type="button"
            title={cat.label}
            onClick={() => setActiveKey(cat.key)}
            className={`flex-1 h-8 flex items-center justify-center rounded-lg text-[16px]
              transition-colors ${
                cat.key === activeKey
                  ? "bg-primary_tint"
                  : "hover:bg-surface"
              }`}
          >
            <span className={cat.key === activeKey ? "" : "opacity-70"}>{cat.icon}</span>
          </button>
        ))}
      </div>

      {/* Emoji grid */}
      <div className="px-2 py-2">
        <p className="px-1 pb-1 text-[10.5px] font-semibold uppercase tracking-wide text-text_muted">
          {active.label}
        </p>
        <div className="grid grid-cols-8 gap-0.5 max-h-[200px] overflow-y-auto custom-scrollbar">
          {active.emojis.map((emoji, i) => (
            <button
              key={`${active.key}-${i}`}
              type="button"
              onClick={() => onSelect(emoji)}
              className="w-full aspect-square flex items-center justify-center rounded-lg
                text-[19px] hover:bg-surface_alt active:scale-90 transition-all"
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmojiPicker;
